import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-services/commerce-services/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "import-api"
    }}>{`Import API`}</h1>
    <p>{`The Adobe Commerce Reporting Import API allows you to push arbitrary data into your data warehouse using REST.`}</p>
    <p>{`Before using the import API, make sure you `}<a parentName="p" {...{
        "href": "../reporting/index.md#authentication"
      }}>{`authenticate`}</a>{` your connection.`}</p>
    <h2 {...{
      "id": "return-codes"
    }}>{`Return Codes`}</h2>
    <p>{`The Data Import API uses standard HTTP return codes to indicate the status of a request. Your application should handle each of the following return statuses.`}</p>
    <p>{`Codes in the 2xx range indicate a successful transaction, codes in the 4xx range indicate a bad request, and codes in the 5xx range indicate an error with Adobe Commerce Reporting. If errors in the 5xx range persist, please contact `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/commerce-knowledge-base/kb/help-center-guide/magento-help-center-user-guide.html"
      }}>{`support`}</a>{`.`}</p>
    <ul>
      <li parentName="ul">{`200 OK - request was successful.`}</li>
      <li parentName="ul">{`201 Created - new data was added as a result of the request.`}</li>
      <li parentName="ul">{`400 Bad request - Your request was missing a required parameter.`}</li>
      <li parentName="ul">{`401 Unauthorized - Authorization failed. Double check your API key.`}</li>
      <li parentName="ul">{`404 Not Found - The resource you are looking for does not exist.`}</li>
      <li parentName="ul">{`500 Server Error - There is an error in Adobe Commerce Reporting.`}</li>
    </ul>
    <h2 {...{
      "id": "versioning"
    }}>{`Versioning`}</h2>
    <p>{`The current version of the Import API is v2.`}</p>
    <p>{`v1 is still available, but will be deprecated in the future.`}</p>
    <h2 {...{
      "id": "test-environment"
    }}>{`Test environment`}</h2>
    <p>{`The Data Import API has a full test (sandbox) environment.`}</p>
    <p>{`The sandbox environment uses the same keys and return codes as the production API, but does not persist incoming data. You can use this environment to test your integration.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -v https://sandbox-connect.rjmetrics.com/v2/client/:cid/:endpoint?apikey=:apikey
`}</code></pre>
    <h2 {...{
      "id": "methods"
    }}>{`Methods`}</h2>
    <h3 {...{
      "id": "status"
    }}>{`Status`}</h3>
    <p>{`You can always check the status of the Data Import API. This is called when you instantiate the client. This will return a `}<inlineCode parentName="p">{`200-Success`}</inlineCode>{` response if the API is operational.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -v https://connect.rjmetrics.com
`}</code></pre>
    <h3 {...{
      "id": "upsert"
    }}>{`Upsert`}</h3>
    <p>{`The upsert method allows you to push data into your RJMetrics data warehouse. You can push entire arrays of data or single data points. This endpoint will only accept data that have the following properties:`}</p>
    <ul>
      <li parentName="ul">{`The data must be valid JSON.`}</li>
      <li parentName="ul">{`Each data point must contain a `}<inlineCode parentName="li">{`keys`}</inlineCode>{` field. The `}<inlineCode parentName="li">{`keys`}</inlineCode>{` field should specify which fields in the records represent the primary key(s).`}</li>
      <li parentName="ul">{`An array of data must contain no more than 100 individual data points.`}</li>
    </ul>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`Each data point in your data warehouse will be uniquely indexed by the fields specified in `}<inlineCode parentName="p">{`keys`}</inlineCode>{`. If a new data point has keys that conflict with a pre-existing data point, the old data point will be replaced.`}</p>
    <p>{`Tables in the Data Import API are schemaless. There is no command to create or destroy a table - you can push data to any table name and it will be dynamically generated.`}</p>
    <p>{`Here are some guidelines for managing tables:`}</p>
    <ul>
      <li parentName="ul">{`Create one table for each type of data point you are pushing.`}</li>
      <li parentName="ul">{`Generally speaking, each data point pushed into a table should have the same schema.`}</li>
      <li parentName="ul">{`Typically, one type of 'thing' will correspond to one table. For example, a typical eCommerce company might have a 'customer', 'order', 'order_item', and 'product' table.`}</li>
      <li parentName="ul">{`Table names must be alphanumeric (plus underscores). Bad table names will result in a `}<inlineCode parentName="li">{`400 Bad Request`}</inlineCode>{` return code.`}</li>
    </ul>
    <p>{`Here's an example of an Upsert call:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -X POST -d @filename https://connect.rjmetrics.com/v2/client/:cid/table/:table/data?apikey=:apikey --header "Content-type: application/json"
`}</code></pre>
    <p>{`The above call contains the following variables:`}</p>
    <p><inlineCode parentName="p">{`@filename`}</inlineCode>{` - name of the file you are uploading
`}<inlineCode parentName="p">{`:cid`}</inlineCode>{` - your client Id
`}<inlineCode parentName="p">{`:table`}</inlineCode>{` - table name
`}<inlineCode parentName="p">{`:apikey`}</inlineCode>{` - your API key`}</p>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"status": "complete",
"created_at": "2019-08-05 04:51:02"
client.push_data(
    "table_name",
    test_data
)
`}</code></pre>
    <h3 {...{
      "id": "upsert-single-data-point-example"
    }}>{`Upsert single data point example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "keys": ["id"],
  "id": 1,
  "email": "joe@schmo.com",
  "status": "pending",
  "created_at": "2019-08-01 14:22:32"
}
`}</code></pre>
    <h3 {...{
      "id": "upsert-array-of-data-points-example"
    }}>{`Upsert array of data points example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`[{
  "keys": ["id"],
  "id": 1,
  "email": "joe@schmo.com",
  "status": "pending",
  "created_at": "2019-08-01 14:22:32"
},{
  "keys": ["id"],
  "id": 2,
  "email": "anne@schmo.com",
  "status": "pending",
  "created_at": "2019-08-03 23:12:30"
},{
  "keys": ["id"],
  "id": 1,
  "email": "joe@schmo.com",
  "status": "complete",
  "created_at": "2019-08-05 04:51:02"
}]
`}</code></pre>
    <h2 {...{
      "id": "additional-examples"
    }}>{`Additional Examples`}</h2>
    <p>{`The following section describes how you can call the import API through various libraries to perform the following tasks:`}</p>
    <ul>
      <li parentName="ul">{`Create a Users Table`}</li>
      <li parentName="ul">{`Create an Orders Table`}</li>
    </ul>
    <h3 {...{
      "id": "create-a-users-table"
    }}>{`Create a users table`}</h3>
    <p>{`Your most important table in Adobe Commerce Reporting is your `}<inlineCode parentName="p">{`Users`}</inlineCode>{` table. In your application, you probably have a `}<inlineCode parentName="p">{`user`}</inlineCode>{` object with some data like `}<inlineCode parentName="p">{`id`}</inlineCode>{`, `}<inlineCode parentName="p">{`email`}</inlineCode>{`, and `}<inlineCode parentName="p">{`acquisition_source`}</inlineCode>{`.`}</p>
    <p>{`This examples describes how to push this data to the Import API.`}</p>
    <p>{`First, define a template to push the data. Ensure the client is `}<inlineCode parentName="p">{`authenticated <api-authentication>`}</inlineCode>{` and prepare to push the data to the Import API.`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript"
    }}>{`Javascript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`var rjmetrics = require("rjmetrics");
client = new rjmetrics.Client(your-client-id, "your-api-key");

// make sure the client is authenticated before we do anything
client.authenticate().then( function(data) {
  // this is where we'll push the data
}).fail(function(err) {
  console.error("Failed to authenticate!");
});
`}</code></pre>
    <h4 {...{
      "id": "php"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`require 'vendor/autoload.php';
$client = new RJMetrics\\Client($your-client-id, "your-api-key");

// make sure the client is authenticated before we do anything
if($client->authenticate()) {
  // this is where we'll push the data
}
`}</code></pre>
    <h4 {...{
      "id": "clojure"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`(ns examples.acquisition-source
  (:require [rjmetrics.core :as rjmetrics]))

(defn run
  []
  (let [config {:client-id your-client-id :api-key "your-api-key"}]
    (when (rjmetrics/authenticated? config)
      ;; this is where we'll push data
    )
  )
)
`}</code></pre>
    <h4 {...{
      "id": "ruby"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`require "rjmetrics_client"
client = RJMetricsClient.new(your-client-id, "your-api-key")

# make sure the client is authenticated before we do anything
if client.authenticated?
  # this is where we'll push data
end
`}</code></pre>
    <h4 {...{
      "id": "python"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import rjmetrics.client
client = rjmetrics.client.Client(your_client_id, "your_api_key")

# make sure the client is authenticated before we do anything
if client.authenticate()
 # this is where we'll push data
`}</code></pre>
    <p>{`Next, create a new function to sync the new data.`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-1"
    }}>{`Javascript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function syncUser(client, user) {
  return client.pushData(
    // table named "users"
    "users",
    {
      // user_id is the unique key here, since each user should only
      // have one record in this table
      "keys": ["id"],
      "id": user.id,
      "email": user.email,
      "acquisition_source": user.acquisition_source
    });
}
`}</code></pre>
    <h4 {...{
      "id": "php-1"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`function syncUser($client, $user) {
  $dataToPush = new stdClass();
  $dataToPush->id = $user->id;
  $dataToPush->email = $user->email;
  $dataToPush->acquisition_source = $user->acquisitionSource;
  // user_id is the unique key here, since each user should only
  // have one record in this table
  $dataToPush->keys = array("id");

  // table named "users"
  return $client->pushData("users", $dataToPush);
}
`}</code></pre>
    <h4 {...{
      "id": "clojure-1"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`(defn- sync-user
  [config user]
  (let [result (rjmetrics/push-data config
                                    ;; table named "users"
                                    "users"
                                    ;; user_id is the unique key here, since each user
                                    ;; should only have one record in the table
                                    (assoc user :keys ["id"]))]
    (if (= (-> result first :status) 201)
        (print "Synced user with id" (:id user) "\\n")
        (print "Failed to sync user with id" (:id user) "\\n"))))
`}</code></pre>
    <h4 {...{
      "id": "ruby-1"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`def sync_user(client, user)
  # \`id\` is the unique key here, since each user should only
  # have one record in this table
  user[:keys] = [:id]
  # table named "users"
  return client.pushData("users", user)
end
`}</code></pre>
    <h4 {...{
      "id": "python-1"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def sync_user(client, user):
  # \`id\` is the unique key here, since each user should only
  # have one record in this table
  user["keys"] = ["id"]
  # table named "users"
  return client.push_data("users", [user]) # NOTE: the python library only pushes lists
`}</code></pre>
    <p>{`Finally, incorporate the `}<inlineCode parentName="p">{`syncUser`}</inlineCode>{` function into the original script.`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-2"
    }}>{`Javascript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`var rjmetrics = require("rjmetrics");
var client = new rjmetrics.Client(your-client-id, "your-api-key");

function syncUser(client, user) {
  return client.pushData(
    // table named "users"
    "users",
    {
      // user_id is the unique key here, since each user should only
      // have one record in this table
      "keys": ["id"],
      "id": user.id,
      "email": user.email,
      "acquisition_source": user.acquisition_source
    });
}

// let's define some fake users
var users = [
  {id: 1, email: "joe@schmo.com", acquisition_source: "PPC"},
  {id: 2, email: "mike@smith.com", acquisition_source: "PPC"},
  {id: 3, email: "lorem@ipsum.com", acquisition_source: "Referral"},
  {id: 4, email: "george@vandelay.com", acquisition_source: "Organic"},
  {id: 5, email: "larry@google.com", acquisition_source: "Organic"},
];

// make sure the client is authenticated before we do anything
client.authenticate().then( function(data) {

  // iterate through users and push data
  users.forEach( function(user) {
    syncUser(client, user).then( function(data) {
      console.log("Synced user with id " + user.id);
    }, function(error) {
      console.error("Failed to sync user with id " + user.id);
    })
  });

}).fail(function(err) {
  console.error("Failed to authenticate!");
});
`}</code></pre>
    <h4 {...{
      "id": "php-2"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`require 'vendor/autoload.php';
$client = new RJMetrics\\Client($your-client-id, "your-api-key");

function syncUser($client, $user) {
  $dataToPush = new stdClass();
  $dataToPush->id = $user->id;
  $dataToPush->email = $user->email;
  $dataToPush->acquisition_source = $user->acquisitionSource;
  // user_id is the unique key here, since each user should only
  // have one record in this table
  $dataToPush->keys = array("id");

  // table named "users"
  return $client->pushData("users", $dataToPush);
}

// let's define some fake users
function fakeUserGenerator($id, $email, $acquisitionSource) {
  $toReturn = new stdClass();

  $toReturn->id = $id;
  $toReturn->email = $email;
  $toReturn->acquisitionSource = $acquisitionSource;

  return $toReturn;
}

$users = array(
  fakeUserGenerator(1, "joe@schmo.com", "PPC"),
  fakeUserGenerator(2, "mike@smith.com", "PPC"),
  fakeUserGenerator(3, "lorem@ipsum.com", "Referral"),
  fakeUserGenerator(4, "george@vandelay.com", "Organic"),
  fakeUserGenerator(5, "larry@google.com", "Organic"),
);

// make sure the client is authenticated before we do anything
if($client->authenticate()) {
  // iterate through users and push data
  foreach($users as $user) {
    $responses = syncUser($client, $user);

    // api calls always return an array of responses
    foreach($responses as $response) {
      if($response->code == 201)
        print("Synced user with id {$user->id}\\n");
      else
        print("Failed to sync user with id {$user->id}\\n");
    }
  }
}
`}</code></pre>
    <h4 {...{
      "id": "clojure-2"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`(ns examples.acquisition-source
  (:require [rjmetrics.core :as rjmetrics]))

(defn- sync-user
  [config user]
  (let [result (rjmetrics/push-data config
                                    ;; table named "users"
                                    "users"
                                    ;; user_id is the unique key here, since each user
                                    ;; should only have one record in the table
                                    (assoc user :keys ["id"]))]
    (if (= (-> result first :status) 201)
        (print "Synced user with id" (:id user) "\\n")
        (print "Failed to sync user with id" (:id user) "\\n"))))

(defn run
  []
  (let [config {:client-id your-client-id :api-key "your-api-key"}
        ;; let's define some fake users
        users [{:id 1, :email "joe@schmo.com", :acquisition_source "PPC"}
                {:id 2, :email "mike@smith.com", :acquisition_source "PPC"}
                {:id 3, :email "lorem@ipsum.com", :acquisition_source "Referral"}
                {:id 4, :email "george@vandelay.com", :acquisition_source "Organic"}
                {:id 5, :email "larry@google.com", :acquisition_source "Organic"}]]
    ;; make sure the client is authenticated before we do anything
    (when (rjmetrics/authenticated? config)
      ;; iterate through users and push data
      (dorun (map (partial sync-user config) users)))))
`}</code></pre>
    <h4 {...{
      "id": "ruby-2"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`require "rjmetrics_client"
client = RJMetricsClient.new(your-api-key, "your-client-id")

# let's define some fake users
fake_users = [
  {:id => 1, :email => "joe@schmo.com", :acquisition_source => "PPC"},
  {:id => 2, :email => "mike@smith.com", :acquisition_source => "PPC"},
  {:id => 3, :email => "lorem@ipsum.com", :acquisition_source => "Referral"},
  {:id => 4, :email => "george@vandelay.com", :acquisition_source => "Organic"},
  {:id => 5, :email => "larry@google.com", :acquisition_source => "Organic"}
]

def sync_user(client, user)
  # \`id\` is the unique key here, since each user should only
  # have one record in this table
  user[:keys] = [:id]
  # table named "users"
  return client.pushData("users", user)
end

# make sure the client is authenticated before we do anything
if client.authenticated?
  fake_users.each do |user|
    # iterate through users and push data
    sync_user(client, user).each do |response|
      if response["code"]
        puts "Synced user with id #{user[:id]}"
      else
        puts "Failed to sync user with id #{user[:id]}"
      end
    end
  end
end
`}</code></pre>
    <h4 {...{
      "id": "python-2"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import rjmetrics.client

CLIENT_ID = 0000
API_KEY = 'your_api_key'

client = rjmetrics.client.Client(CLIENT_ID, API_KEY)

# let's define some fake users
fake_users = [
  {"id": 1, "email": "joe@schmo.com", "acquisition_source": "PPC"},
  {"id": 2, "email": "mike@smith.com", "acquisition_source": "PPC"},
  {"id": 3, "email": "lorem@ipsum.com", "acquisition_source": "Referral"},
  {"id": 4, "email": "george@vandelay.com", "acquisition_source": "Organic"},
  {"id": 5, "email": "larry@google.com", "acquisition_source": "Organic"}
]


def sync_user(client, user):
  # \`id\` is the unique key here, since each user should only
  # have one record in this table
  user["keys"] = ["id"]
  # table named "users"
  return client.push_data("users", [user])[0]


# make sure the client is authenticated before we do anything
if client.authenticate():
  for user in fake_users:
    # iterate through users and push data
    response = sync_user(client, user)
    if response.ok:
        print "Synced user with id ", user["id"]
    else:
        print "Failed to sync user with id ", user["id"]
`}</code></pre>
    <p>{`This example is included with the `}<a parentName="p" {...{
        "href": "libraries.md"
      }}>{`client libraries`}</a>{` or can be `}<a parentName="p" {...{
        "href": "https://github.com/rjmetrics"
      }}>{`downloaded from github`}</a>{`.`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-3"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install
node users-table.js
`}</code></pre>
    <h4 {...{
      "id": "php-3"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`composer install
php users-table.php
`}</code></pre>
    <h4 {...{
      "id": "clojure-3"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`lein repl

> (ns examples.users-table)
> (require :reload 'examples.users-table)
> (run)
`}</code></pre>
    <h4 {...{
      "id": "ruby-3"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bundle install
ruby users-table.rb
`}</code></pre>
    <h4 {...{
      "id": "python-3"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`pip install
python examples/users_table.py
`}</code></pre>
    <h3 {...{
      "id": "create-an-orders-table"
    }}>{`Create an orders table`}</h3>
    <p>{`Now, create an orders table with the following fields: `}<inlineCode parentName="p">{`id`}</inlineCode>{`, `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`value`}</inlineCode>{` and `}<inlineCode parentName="p">{`sku`}</inlineCode>{`.`}</p>
    <p>{`Create a new function to push the order object:`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-4"
    }}>{`Javascript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function syncOrder(client, order) {
  return client.pushData(
    "orders",
    {
      "keys": ["id"],
      "id": order.id,
      "user_id": order.user_id,
      "value": order.value,
      "sku": order.sku
    });
}
`}</code></pre>
    <h4 {...{
      "id": "php-4"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`function syncOrder($client, $order) {
  $dataToPush = new stdClass();
  $dataToPush->id = $order->id;
  $dataToPush->user_id = $order->user_id;
  $dataToPush->value = $order->value;
  $dataToPush->sku = $order->sku;
  $dataToPush->keys = array("id");

  return $client->pushData("orders", $dataToPush);
}
`}</code></pre>
    <h4 {...{
      "id": "clojure-4"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`(defn- sync-order
  [config order]
  (let [result (rjmetrics/push-data config
                                    "orders"
                                    (assoc order :keys ["id"]))]
    (if (= (-> result first :status) 201)
        (print "Synced order with id" (:id order) "\\n")
        (print "Failed to sync order with id" (:id order) "\\n"))))
`}</code></pre>
    <h4 {...{
      "id": "ruby-4"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`def sync_order(client, order)
  order[:keys] = [:id]
  return client.pushData("orders", order)
end
`}</code></pre>
    <h4 {...{
      "id": "python-4"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def sync_order(client, order):
  order["keys"] = ["id"]
  return client.push_data("orders", [order])[0]
`}</code></pre>
    <p>{`Now, plug this into the same template from the `}<inlineCode parentName="p">{`users`}</inlineCode>{` table:`}</p>
    <CodeBlock slots="heading, code" repeat="5" languages="JS, PHP, Clojure, Ruby, Python" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-5"
    }}>{`Javascript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`var rjmetrics = require("rjmetrics");
var client = new rjmetrics.Client(your-client-id, "your-api-key");

function syncOrder(client, order) {
  return client.pushData(
    "orders",
    {
      "keys": ["id"],
      "id": order.id,
      "user_id": order.user_id,
      "value": order.value,
      "sku": order.sku
    });
}

var orders = [
  {"id": 1, "user_id": 1, "value": 58.40,  "sku": "milky-white-suede-shoes"},
  {"id": 2, "user_id": 1, "value": 23.99,  "sku": "red-button-down-fleece"},
  {"id": 3, "user_id": 2, "value": 5.00,   "sku": "bottle-o-bubbles"},
  {"id": 4, "user_id": 3, "value": 120.01, "sku": "zebra-striped-game-boy"},
  {"id": 5, "user_id": 5, "value": 9.90  , "sku": "kitten-mittons"}
];

client.authenticate().then( function(data) {

  orders.forEach( function(order) {
    syncOrder(client, order).then( function(data) {
      console.log("Synced order with id " + order.id);
    }, function(error) {
      console.error("Failed to sync order with id " + order.id);
    })
  });

}).fail(function(err) {
  console.error("Failed to authenticate!");
});
`}</code></pre>
    <h4 {...{
      "id": "php-5"
    }}>{`PHP`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`require 'vendor/autoload.php';
$client = new RJMetrics\\Client($your-client-id, "your-api-key");

function syncOrder($client, $order) {
  $dataToPush = new stdClass();
  $dataToPush->id = $order->id;
  $dataToPush->user_id = $order->user_id;
  $dataToPush->value = $order->value;
  $dataToPush->sku = $order->sku;
  $dataToPush->keys = array("id");

  return $client->pushData("orders", $dataToPush);
}

function fakeOrderGenerator($id, $userId, $value, $sku) {
  $toReturn = new stdClass();

  $toReturn->id = $id;
  $toReturn->user_id = $userId;
  $toReturn->value = $value;
  $toReturn->sku = $sku;

  return $toReturn;
}

$orders = array(
  fakeOrderGenerator(1, 1, 58.40, "milky-white-suede-shoes"),
  fakeOrderGenerator(2, 1, 23.99, "red-buttons-down-fleece"),
  fakeOrderGenerator(3, 2, 5.00, "bottle-o-bubbles"),
  fakeOrderGenerator(4, 3, 120.01, "zebra-striped-game-boy"),
  fakeOrderGenerator(5, 5, 9.90, "kitten-mittons")
);

if($client->authenticate()) {
  foreach($orders as $order) {
    $responses = syncOrder($client, $order);

    foreach($responses as $response) {
      if($response->code == 201)
        print("Synced order with id {$order->id}\\n");
      else
        print("Failed to sync order with id {$order->id}\\n");
    }
  }
}
`}</code></pre>
    <h4 {...{
      "id": "clojure-5"
    }}>{`Clojure`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-clojure"
      }}>{`(ns examples.orders-table
  (:require [rjmetrics.core :as rjmetrics]))

(defn- sync-order
  [config order]
  (let [result (rjmetrics/push-data config
                                    "orders"
                                    (assoc order :keys ["id"]))]
    (if (= (-> result first :status) 201)
        (print "Synced order with id" (:id order) "\\n")
        (print "Failed to sync orfer with id" (:id order) "\\n"))))

(defn run
  []
  (let [config {:client-id your-client-id :api-key "your-api-key"}
        orders [{:id 1, :user_id 1 :value 58.40  :sku "milky-white-suede-shoes"}
                {:id 2, :user_id 1 :value 23.99  :sku "red-button-down-fleece"}
                {:id 3, :user_id 2 :value 5.00   :sku "bottle-o-bubbles"}
                {:id 4, :user_id 3 :value 120.01 :sku "zebra-striped-game-boy"}
                {:id 5, :user_id 5 :value 9.90   :sku "kitten-mittons"}]]
    (when (rjmetrics/authenticated? config)
      (dorun (map (partial sync-order config) users)))))
`}</code></pre>
    <h4 {...{
      "id": "ruby-5"
    }}>{`Ruby`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`require "rjmetrics_client"
client = RJMetricsClient.new(your-client-id, "your-api-key")

fake_orders = [
  {:id => 1, :user_id => 1, :value => 58.40,  :sku => "milky-white-suede-shoes"},
  {:id => 2, :user_id => 1, :value => 23.99,  :sku => "red-button-down-fleece"},
  {:id => 3, :user_id => 2, :value => 5.00,   :sku => "bottle-o-bubbles"},
  {:id => 4, :user_id => 3, :value => 120.01, :sku => "zebra-striped-game-boy"},
  {:id => 5, :user_id => 5, :value => 9.90,   :sku => "kitten-mittons"}
]

def sync_order(client, order)
  order[:keys] = [:id]
  return client.pushData("orders", order)
end

if client.authenticated?
  fake_orders.each do |order|
    sync_order(client, order).each do |response|
      if response["code"]
        puts "Synced order with id #{order[:id]}"
      else
        puts "Failed to sync order with id #{order[:id]}"
      end
    end
  end
end
`}</code></pre>
    <h4 {...{
      "id": "python-5"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import rjmetrics.client

CLIENT_ID = 0000
API_KEY = 'your_api_key'

client = rjmetrics.client.Client(CLIENT_ID, API_KEY)

fake_orders = [
  {"id": 1, "user_id": 1, "value": 58.40,  "sku": "milky-white-suede-shoes"},
  {"id": 2, "user_id": 1, "value": 23.99,  "sku": "red-button-down-fleece"},
  {"id": 3, "user_id": 2, "value": 5.00,   "sku": "bottle-o-bubbles"},
  {"id": 4, "user_id": 3, "value": 120.01, "sku": "zebra-striped-game-boy"},
  {"id": 5, "user_id": 5, "value": 9.90,   "sku": "kitten-mittons"}
]


def sync_order(client, order):
  order["keys"] = ["id"]
  return client.push_data("orders", [order])[0]


# make sure the client is authenticated before we do anything
if client.authenticate():
  for order in fake_orders:
    # iterate through users and push data
    response = sync_order(client, order)
    if response.ok:
        print "Synced order with id ", order["id"]
    else:
        print "Failed to sync order with id ", order["id"]
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      